<template>
  <div class="paypal-button-container">
    <div id="paypal-button"></div>
    <div v-if="payLaterAvailable" class="paypal-pay-later-label">
      {{ payLaterText }}
    </div>
    <div class="paypal-shim" :class="{ 'active': !isSubmitted }">
      <div v-if="isSubmitted" class="disabled"></div>
      <div class="title"><slot /></div>
      <img class="lazy image" :data-src="cdn_url + '/assets/images/paypal-highq.png'" />
    </div>
  </div>
</template>

<script>
  import wait from '../../utils/wait';
  import globals from '../../mixins/globals';
  import { muteLeaveModal } from '../../utils/checkout';

  export default {
    name: 'PaypalButton',
    props: [
      'createOrder',
      'onApprove',
      '$vvariant',
      '$vterms',
      '$vdeal',
    ],
    mixins: [
      globals,
    ],

    data() {
      return {
        isSubmitted: false,
        action: null
      };
    },

    watch: {
      '$vdeal.$invalid'() {
        if (this.action && this.isValid()) {
          this.action.enable();
        }
      },
      '$vvariant.$invalid'() {
        if (this.action && this.isValid()) {
          this.action.enable();
        }
      },
      '$vterms.$invalid'() {
        if (this.action && this.isValid()) {
          this.action.enable();
        }
      },
    },

    mounted () {
      wait(
        () => !!window.paypal,
        () => this.initButton(),
      );

      this.lazyload_update();
    },

    updated() {
      this.lazyload_update();
    },

    computed: {
      payLaterAvailable() {
        return js_query_params.pp_bnpl !== '0' && (!js_data.product || js_data.product.is_pp_pl_off !== true) && ['us', 'gb', 'de', 'fr', 'es', 'it', 'au'].indexOf(js_data.country_code) !== -1;
      },
      payLaterText() {
        return this.$t('checkout.paypal_pay_later_label', {
          'AMOUNT': this.$vdeal && this.$vdeal.$model && js_data.product.prices[this.$vdeal.$model] ? js_data.product.prices[this.$vdeal.$model].quarter_value_text : '',
        })
      }
    },

    methods: {
      isValid() {
        return (!this.$vdeal || !this.$vdeal.$invalid) && (!this.$vvariant || !this.$vvariant.$invalid) && (!this.$vterms || !this.$vterms.$invalid);
      },
      initButton () {
        const { createOrder, onApprove } = this;
        const that = this;

        paypal.Buttons({
          onInit(data, actions) {
            that.action = actions;

            if (!that.isValid()) {
              actions.disable();
            }
          },

          createOrder(data, actions) {
            that.isSubmitted = true;

            return createOrder()
              .then(res => {
                return res && res.id || null;
              })
          },

          onClick () {
            that.$emit('click', true);
            muteLeaveModal();
          },

          onApprove (data, actions) {
            return onApprove(data)
              .then(() => {
                setTimeout(() => that.isSubmitted = false, 1000);
              });
          },

          onError(err) {
            that.isSubmitted = false;
          },

          onCancel(data, actions) {
            that.isSubmitted = false;
          },

          // if user closes paypal popup 
          onAuthorizationDismissed() {
            that.isSubmitted = false;
          },

          style: {
            height: 55,
          }
        }).render('#paypal-button');
      }
    },
  };
</script>

<style lang="scss">
  .paypal-button-container {
    margin-bottom: 10px;
    position: relative;
    width: 100%;

    #paypal-button {
      height: 55px;
      overflow: hidden;
    }

    .paypal-pay-later-label {
      font-style: italic;
      text-align: center;
      font-size: 13px;
      margin: 5px 28px;
    }

    .paypal-shim {
      align-items: center;
      background-color: #ffc438;
      background-image: linear-gradient(#fff0a8,#f9b421);
      border: 1px solid #feae01;
      border-radius: 5px;
      display: flex;
      height: 55px;
      justify-content: center;
      left: 0;
      overflow: hidden;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 100;

      &.active {
        pointer-events: none;
      }

      &:before {
        opacity: 0;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'><path fill='%23Ffff' d='M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z'></path></svg>");
        background-size: 10px;
        background-repeat: no-repeat;
        background-position: center;
        content: "";
        width: 0;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 0 50% 50% 0;
        background-color: rgba(255,255,255,.3);
        transition: all .2s linear 0s;

        [dir="rtl"] & {
          content: '\f053';
          border-radius: 50% 0 0 50%;
          left: auto;
          right: 0;
        }
      }

      .title {
        color: #000;
        font-size: 15px;
        font-weight: 700;
        white-space: nowrap;
      }

      .image {
        margin-left: 10px;
        margin-top: -2px;
        max-width: 90px;

        [dir="rtl"] & {
          margin-left: 0;
          margin-right: 10px;
        }
      }

      .disabled {
        background-color: #fff;
        bottom: 0;
        left: 0;
        opacity: .5;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 1;
      }
    }

    &:hover {
      .paypal-shim.active {
        background-image: linear-gradient(#f9b421, #fff0a8);

        &:before {
          opacity: 1;
          width: 30px;
        }
      }
    }
  }

  .tpl-emc1, .tpl-emc1b {
    @media screen and (min-width: 768px) and (max-width: 991px), (max-width: 420px) {
      .paypal-shim {
        flex-direction: column;

        .image {
          margin: 0;
        }
      }
    }
  }

  .tpl-vmc4 {
    @media screen and (min-width: 576px) and (max-width: 800px), (max-width: 460px) {
      .paypal-shim {
        flex-direction: column;

        .image {
          margin: 0;
        }
      }
    }
  }

</style>
